import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within LanguageProvider');
    }
    return context;
};

// 번역 데이터
const translations = {
    ko: {
        // Navigation
        home: '홈',
        settings: '설정',
        
        // Onboarding
        welcome: '안녕하세요! 코딩 교육 플랫폼에 오신 것을 환영해요 🎉',
        whatIsYourName: '이름이 뭐예요?',
        enterNickname: '닉네임을 입력하세요',
        whatAreYouInterestedIn: '무엇에 관심이 있나요?',
        selectInterests: '관심사를 선택해주세요 (최소 1개)',
        chooseTheme: '좋아하는 색상 테마를 선택하세요',
        startLearning: '학습 시작하기',
        
        // Interests
        soccer: '축구',
        game: '게임',
        pokemon: '포켓몬',
        music: '음악',
        art: '미술',
        science: '과학',
        
        // Course List
        helloUser: '안녕하세요, {{name}}님! 🎉',
        codingAdventure: '를 좋아하는 당신을 위한 코딩 모험!',
        all: '전체',
        creation: '만들기',
        gameType: '게임형',
        dataType: '데이터',
        steps: '단계',
        matchingInterests: '✨ 관심사 매칭',
        comingSoon: '준비중',
        
        // Course Detail
        backToCourses: '← 코스 목록',
        startLearning2: '학습 시작하기 →',
        overview: '개요',
        curriculum: '커리큘럼',
        learningConcepts: '학습 개념',
        whatWillWeMake: '🎯 무엇을 만들까요?',
        whatWillWeLearn: '✨ 이런 걸 배워요',
        finalResult: '🎨 최종 결과물',
        stepCurriculum: '📚 {{count}}단계 커리큘럼',
        readingType: '📖 읽기',
        codingType: '💻 코딩',
        concepts: '💡 학습 개념',
        nextRecommendedCourses: '🚀 다음 추천 코스',
        
        // Course Template
        step: '단계',
        mission: '📝 미션',
        tips: '💡 팁',
        hints: '🔍 힌트',
        showHint: '힌트 보기',
        showMoreHint: '다음 힌트',
        runCode: '▶️ 실행',
        running: '실행 중...',
        executionResult: '실행 결과',
        codeWillShowHere: '코드를 실행하면 결과가 여기에 표시됩니다.',
        checkPoints: '📋 체크포인트',
        previous: '이전',
        next: '다음',
        complete: '완료',
        pythonEditor: 'Python 코드 에디터',
        preparingPython: 'Python 환경을 준비중입니다. 잠시만 기다려주세요...',
        error: '에러',
        
        // Settings Modal
        settingsTitle: '설정',
        nickname: '닉네임',
        save: '저장',
        edit: '수정',
        theme: '테마',
        language: '언어',
        interests: '관심사',
        saveChanges: '변경사항 저장',
        
        // Difficulty
        beginner: '초급',
        intermediate: '중급',
        advanced: '고급',
        
        // Loading & Errors
        loadingCourseInfo: '코스 정보를 불러오는 중...',
        courseNotFound: '코스를 찾을 수 없습니다.',
        loadingContent: '학습 내용을 불러오는 중...',
        stepNotFound: '스텝을 찾을 수 없습니다.',
        loadingEnvironment: '학습 환경을 준비하고 있어요...',
        somethingWentWrong: '앗! 문제가 발생했어요 😅',
        tryAgainLater: '페이지를 새로고침하거나 잠시 후 다시 시도해주세요.',
        refreshPage: '페이지 새로고침',
        pageNotFound: '페이지를 찾을 수 없어요',
        pageNotFoundDesc: '요청하신 페이지가 존재하지 않습니다.',
        goToHome: '홈으로 돌아가기'
    },
    en: {
        // Navigation
        home: 'Home',
        settings: 'Settings',
        
        // Onboarding
        welcome: 'Welcome to the Coding Education Platform! 🎉',
        whatIsYourName: 'What\'s your name?',
        enterNickname: 'Enter your nickname',
        whatAreYouInterestedIn: 'What are you interested in?',
        selectInterests: 'Select your interests (at least 1)',
        chooseTheme: 'Choose your favorite color theme',
        startLearning: 'Start Learning',
        
        // Interests
        soccer: 'Soccer',
        game: 'Game',
        pokemon: 'Pokemon',
        music: 'Music',
        art: 'Art',
        science: 'Science',
        
        // Course List
        helloUser: 'Hello, {{name}}! 🎉',
        codingAdventure: 'Coding adventure for you who love',
        all: 'All',
        creation: 'Creation',
        gameType: 'Game',
        dataType: 'Data',
        steps: 'steps',
        matchingInterests: '✨ Matching Interests',
        comingSoon: 'Coming Soon',
        
        // Course Detail
        backToCourses: '← Course List',
        startLearning2: 'Start Learning →',
        overview: 'Overview',
        curriculum: 'Curriculum',
        learningConcepts: 'Learning Concepts',
        whatWillWeMake: '🎯 What will we make?',
        whatWillWeLearn: '✨ What will we learn',
        finalResult: '🎨 Final Result',
        stepCurriculum: '📚 {{count}}-step Curriculum',
        readingType: '📖 Reading',
        codingType: '💻 Coding',
        concepts: '💡 Concepts',
        nextRecommendedCourses: '🚀 Next Recommended Courses',
        
        // Course Template
        step: 'Step',
        mission: '📝 Mission',
        tips: '💡 Tips',
        hints: '🔍 Hints',
        showHint: 'Show Hint',
        showMoreHint: 'Next Hint',
        runCode: '▶️ Run',
        running: 'Running...',
        executionResult: 'Execution Result',
        codeWillShowHere: 'Code execution results will appear here.',
        checkPoints: '📋 Checkpoints',
        previous: 'Previous',
        next: 'Next',
        complete: 'Complete',
        pythonEditor: 'Python Code Editor',
        preparingPython: 'Preparing Python environment. Please wait...',
        error: 'Error',
        
        // Settings Modal
        settingsTitle: 'Settings',
        nickname: 'Nickname',
        save: 'Save',
        edit: 'Edit',
        theme: 'Theme',
        language: 'Language',
        interests: 'Interests',
        saveChanges: 'Save Changes',
        
        // Difficulty
        beginner: 'Beginner',
        intermediate: 'Intermediate',
        advanced: 'Advanced',
        
        // Loading & Errors
        loadingCourseInfo: 'Loading course information...',
        courseNotFound: 'Course not found.',
        loadingContent: 'Loading content...',
        stepNotFound: 'Step not found.',
        loadingEnvironment: 'Preparing learning environment...',
        somethingWentWrong: 'Oops! Something went wrong 😅',
        tryAgainLater: 'Please refresh the page or try again later.',
        refreshPage: 'Refresh Page',
        pageNotFound: 'Page Not Found',
        pageNotFoundDesc: 'The page you requested does not exist.',
        goToHome: 'Go to Home'
    }
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem('language') || 'ko';
    });

    useEffect(() => {
        localStorage.setItem('language', language);
    }, [language]);

    const t = (key, params = {}) => {
        let text = translations[language][key] || translations['ko'][key] || key;
        
        // 파라미터 치환 (예: {{name}} -> 실제 이름)
        Object.keys(params).forEach(param => {
            text = text.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
        });
        
        return text;
    };

    const changeLanguage = (newLanguage) => {
        if (translations[newLanguage]) {
            setLanguage(newLanguage);
        }
    };

    const value = {
        language,
        changeLanguage,
        t,
        availableLanguages: Object.keys(translations)
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};